import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useQueryClient } from '@tanstack/react-query';
import { IconCheck } from '@tabler/icons-react';
import { MealPlanningWizard } from '../components/MealPlanning/MealPlanningWizard';
import { usePageTitle } from '../hooks/usePageTitle';
import { useAppUser } from '../hooks/useAppUser';
import { useAuth } from '../hooks/useAuth';

export const MealPlanningPage = () => {
  usePageTitle('Plan Your Meals | Meals You Love');
  const navigate = useNavigate();
  const { user } = useAppUser();
  const { user: authUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();

  // Launch-email entry: ?launch=<announcement id>. The claim call marks the
  // in-app announcement seen (so email entrants never also get the dashboard
  // modal) and re-ups the trial for expired accounts. Fire-and-forget with a
  // one-shot guard; the param is stripped so refreshes do not re-claim.
  const launchClaimRef = useRef(false);
  useEffect(() => {
    const launchId = searchParams.get('launch');
    if (!launchId || !authUser || launchClaimRef.current) return;
    launchClaimRef.current = true;
    (async () => {
      try {
        const token = await authUser.getIdToken();
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/user/launch-claim`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ announcementId: launchId }),
        });
        if (response.ok) {
          const data = await response.json();
          if (data.trialExtended) {
            // The renewal happened server-side; the cached subscription state
            // still says expired and would bounce post-creation navigation to
            // the billing page until a refetch
            queryClient.invalidateQueries({ queryKey: ['subscription'] });
            notifications.show({
              title: 'Welcome back',
              message: 'Your trial is renewed for the next two weeks. Enjoy!',
              color: 'green',
              icon: <IconCheck size={18} />,
              autoClose: 6000,
            });
          }
        }
      } catch (error) {
        console.error('Failed to process launch claim:', error);
      } finally {
        setSearchParams((params) => {
          params.delete('launch');
          return params;
        }, { replace: true });
      }
    })();
  }, [searchParams, authUser, setSearchParams, queryClient]);

  const handleComplete = (planId: string, failed?: boolean) => {
    navigate('/', { state: { showTasteTuner: user?.tasteTunerDismissed !== true } });
  };

  const handleCancel = () => {
    navigate(-1);
  };

  return (
    <Box mx={{ base: 0, sm: 'calc(var(--mantine-spacing-lg) * -1)', md: 'calc(var(--mantine-spacing-xl) * -1)' }}>
      <MealPlanningWizard
        onComplete={handleComplete}
        onCancel={handleCancel}
      />
    </Box>
  );
};